<script lang="ts">
  import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
  import { 
    Palette, 
    Layout, 
    CheckCircle2, 
    Eye,
    Sparkles,
    Zap,
    Code2,
    MonitorSmartphone,
    FileText,
    TrendingUp,
    Users,
    Clock,
    Shield,
    AlertTriangle,
    Target,
    BarChart3
  } from '@lucide/svelte';
	import { createPageSEO, createWebPageSchema, createBreadcrumbSchema, createFAQSchema } from '$lib/config/seo';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

  const faqs = [
    {
      question: "How do you evaluate frontend developer design skills?",
      answer: "We analyze deployed projects with automated Lighthouse audits for performance, accessibility, and SEO. You can test their live sites yourself - check responsiveness on your phone, evaluate visual design, test interactions. We provide the technical metrics, you judge the aesthetic quality."
    },
    {
      question: "Can I see actual websites frontend developers have built?",
      answer: "Absolutely. Every frontend developer profile includes links to live deployed projects, GitHub repositories with demo links, and portfolio websites. You can interact with their actual work - test responsiveness, check loading performance, and evaluate design quality firsthand."
    },
    {
      question: "What code quality issues can you detect automatically?",
      answer: "We run ESLint analysis, check for security issues (hardcoded secrets, unsafe HTML), detect code duplication, verify TypeScript usage and strictness, analyze bundle sizes, check for modern patterns like dynamic imports and code splitting, and flag outdated dependencies or framework patterns."
    },
    {
      question: "How do you determine if someone is actually a senior developer?",
      answer: "We don't claim to perfectly assess seniority - that requires human judgment. But we provide strong indicators: test coverage, error handling patterns, performance optimization evidence, security awareness, code organization quality, commit patterns, and ratio of features to rewrites. These signals help you focus interviews on the right candidates."
    },
    {
      question: "Do you verify React, Vue, or Angular expertise?",
      answer: "We verify they've used the framework in real projects, analyze their component patterns, check for modern vs outdated approaches (hooks vs classes, composition API vs options), detect common anti-patterns, and measure code quality. The code is there for you to review - we just organize it so you can quickly assess whether it's worth your time."
    },
    {
      question: "Can you really tell code quality from automated analysis?",
      answer: "Partially. We can objectively flag problems: security issues, performance red flags, duplicated code, missing tests, unsafe patterns. We can't judge architectural decisions without context - but we surface patterns worth discussing. Think of it as intelligent filtering: we help you spend time on candidates who clear basic quality bars."
    }
  ];

  const seo = createPageSEO({
    title: 'Hire Frontend Developers - Portfolio & Code Quality Verified | TalentProfile',
    description: 'Find frontend developers with deployed projects and analyzed code quality. Automated security, performance, and pattern analysis. Review real code before interviewing. Free.',
    canonical: '/hire-frontend-developers',
    keywords: 'hire frontend developers, React developer jobs, Vue.js recruitment, Angular developer hiring, UI developer jobs, code quality analysis, portfolio verified developers',
    structuredData: [
      createWebPageSchema({
        name: 'Hire Frontend Developers',
        description: 'Recruit frontend developers based on analyzed code quality, portfolios, and deployed projects',
        url: '/hire-frontend-developers'
      }),
      createBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Hire', url: '/hire' },
        { name: 'Frontend Developers' }
      ]),
      createFAQSchema(faqs)
    ]
  });

  const stats = [
    { value: "18+", label: "Code Quality Checks", description: "Automated analysis per repository" },
    { value: "2.1x", label: "Faster Screening", description: "vs manual code review of every candidate" },
    { value: "87%", label: "Red Flags Caught", description: "Security & quality issues before interviews" },
    { value: "95%", label: "Portfolio Accuracy", description: "Verified deployed projects actually work" }
  ];

  const codeAnalysis = [
    {
      icon: Shield,
      category: "Security Analysis",
      checks: [
        "Hardcoded API keys and secrets",
        "Unsafe dangerouslySetInnerHTML usage",
        "SQL injection vulnerabilities",
        "XSS attack vectors",
        "Outdated dependencies with known CVEs",
        "HTTPS enforcement and secure headers"
      ],
      value: "Automated scanning catches security mistakes junior developers make"
    },
    {
      icon: Zap,
      category: "Performance Patterns",
      checks: [
        "Dynamic imports and code splitting",
        "Web Workers for heavy computation",
        "Image optimization and lazy loading",
        "Bundle size and unused dependencies",
        "React.memo and useMemo usage",
        "Lighthouse scores on deployed sites"
      ],
      value: "Identifies developers who care about speed, not just features"
    },
    {
      icon: Code2,
      category: "Code Quality Metrics",
      checks: [
        "ESLint violations and warnings",
        "Code duplication percentage",
        "TypeScript strictness level",
        "Test coverage (if tests exist)",
        "Component complexity scores",
        "Consistent naming conventions"
      ],
      value: "Objective quality indicators reduce subjective guesswork"
    },
    {
      icon: Layout,
      category: "Architecture Patterns",
      checks: [
        "File structure (feature vs domain-first)",
        "State management approach",
        "Component composition patterns",
        "Modern vs outdated framework usage",
        "Error boundary implementation",
        "API integration patterns"
      ],
      value: "Reveals how they think about organizing code at scale"
    }
  ];

  const seniorityIndicators = [
    {
      signal: "Test Coverage",
      junior: "No tests or only happy path",
      senior: "Edge cases covered, integration tests, E2E for critical flows",
      automatable: true
    },
    {
      signal: "Error Handling",
      junior: "Uncaught promises, no loading/error states",
      senior: "Proper error boundaries, user-friendly messages, retry logic",
      automatable: true
    },
    {
      signal: "Performance Awareness",
      junior: "No code splitting, large bundles, unnecessary re-renders",
      senior: "Dynamic imports, memoization, optimized images, web vitals monitoring",
      automatable: true
    },
    {
      signal: "Security Practices",
      junior: "API keys in code, unsafe HTML rendering, no input validation",
      senior: "Environment variables, sanitized inputs, CSP headers, auth patterns",
      automatable: true
    },
    {
      signal: "Code Organization",
      junior: "500+ line components, duplicated logic, inconsistent structure",
      senior: "Reusable components, DRY principles, clear separation of concerns",
      automatable: "Partially"
    },
    {
      signal: "TypeScript Usage",
      junior: "any types everywhere or no TypeScript",
      senior: "Strict mode, proper typing, discriminated unions, type guards",
      automatable: true
    }
  ];

  const realAnalysisExamples = [
    {
      finding: "Security Risk: Hardcoded API Key",
      detail: "Found `const API_KEY = 'sk-abc123...'` in src/utils/api.ts line 4",
      impact: "Immediate red flag - shows lack of security awareness",
      color: "red"
    },
    {
      finding: "Performance Issue: No Code Splitting",
      detail: "348KB bundle size, no dynamic imports detected. All routes loaded upfront.",
      impact: "User experience suffers - worth discussing optimization approach",
      color: "orange"
    },
    {
      finding: "Quality Signal: 82% Test Coverage",
      detail: "Jest tests for components, edge cases handled, error scenarios tested",
      impact: "Strong positive indicator - developer thinks about reliability",
      color: "green"
    },
    {
      finding: "Modern Patterns: TypeScript Strict Mode",
      detail: "tsconfig.json with strict: true, no any types, proper interfaces",
      impact: "Indicates attention to type safety and maintainability",
      color: "green"
    },
    {
      finding: "Architecture: 23% Code Duplication",
      detail: "Similar API call patterns repeated in 8 components without abstraction",
      impact: "Suggests early-career or needs mentorship on DRY principles",
      color: "orange"
    },
    {
      finding: "Lighthouse: 92 Performance Score",
      detail: "Deployed site loads in 1.2s, optimized images, good Core Web Vitals",
      impact: "Proves they care about user experience, not just building features",
      color: "green"
    }
  ];

  const whatWeDo = [
    {
      icon: BarChart3,
      title: "Automated Code Analysis",
      description: "We run ESLint, security scanners, duplicate code detection, and complexity analysis on every repository. Objective metrics you can trust.",
      examples: ["TypeScript strictness", "ESLint violations", "Bundle sizes", "Dependency freshness"]
    },
    {
      icon: Target,
      title: "Pattern Recognition",
      description: "Our analysis identifies modern vs outdated patterns, common anti-patterns, and architectural decisions worth discussing in interviews.",
      examples: ["Hooks vs classes", "State management", "Error handling", "Component organization"]
    },
    {
      icon: MonitorSmartphone,
      title: "Deployed Project Testing",
      description: "We run Lighthouse audits on live sites, verify they actually work, and measure real-world performance. Not just code - actual user experience.",
      examples: ["Core Web Vitals", "Mobile responsiveness", "Accessibility scores", "SEO optimization"]
    },
    {
      icon: Users,
      title: "Experience Indicators",
      description: "We analyze commit patterns, code-to-bug ratios, and technical decision quality. Strong signals for seniority, but you make the final call.",
      examples: ["Test coverage", "Security awareness", "Performance optimization", "Code quality trends"]
    }
  ];

  const painPoints = [
    {
      icon: Eye,
      problem: "Can't Judge Code Quality from Resumes",
      reality: "\"5 years React experience\" could mean anything from expert to barely functional",
      solution: "Automated analysis reveals: TypeScript usage, test coverage, modern patterns, security awareness"
    },
    {
      icon: Clock,
      problem: "Manual Code Review Takes Hours",
      reality: "You can't read every candidate's GitHub - so you interview blindly and waste time",
      solution: "Pre-screening analysis flags quality issues and highlights strong candidates automatically"
    },
    {
      icon: AlertTriangle,
      problem: "Security Mistakes Are Invisible",
      reality: "Hardcoded secrets, unsafe HTML, missing validation - you won't spot these in interviews",
      solution: "Automated security scanning catches dangerous patterns before they join your team"
    },
    {
      icon: Sparkles,
      problem: "Performance Skills Unverified",
      reality: "They say they optimize performance, but do they actually use code splitting, lazy loading, memoization?",
      solution: "Analyze deployed sites with Lighthouse, check for optimization patterns in code"
    }
  ];

  const portfolioTypes = [
    {
      type: "E-commerce Interfaces",
      description: "Product grids, checkout flows, shopping cart experiences with payment integration",
      verification: "We check: Payment library usage, cart state management, responsive product grids"
    },
    {
      type: "SaaS Dashboards",
      description: "Data visualization, complex forms, admin panels, and analytics interfaces",
      verification: "We check: Chart library integration, form validation, table performance"
    },
    {
      type: "Landing Pages",
      description: "Marketing sites with conversion-focused design and smooth scroll interactions",
      verification: "We check: Lighthouse scores, animation libraries, SEO optimization"
    },
    {
      type: "Mobile-First Apps",
      description: "Responsive web applications optimized for mobile touch interactions",
      verification: "We check: Touch event handling, PWA setup, mobile performance metrics"
    }
  ];

  const comparisonPoints = [
    {
      traditional: "Read resumes claiming 'clean code' - schedule interviews hoping it's true",
      talentProfile: "See actual ESLint results, code duplication %, TypeScript strictness before first call"
    },
    {
      traditional: "Discover security issues after hiring (hardcoded keys, XSS vulnerabilities)",
      talentProfile: "Automated security scanning flags dangerous patterns in their public repos"
    },
    {
      traditional: "Ask hypothetical questions about performance optimization",
      talentProfile: "See Lighthouse scores on their deployed sites + check for code splitting in repos"
    },
    {
      traditional: "Wonder if they write tests or just claim to",
      talentProfile: "View actual test coverage, see edge cases they considered, evaluate test quality"
    },
    {
      traditional: "Guess if they're mid-level or senior based on years of experience",
      talentProfile: "Review seniority indicators: error handling, security awareness, architecture decisions"
    },
    {
      traditional: "Hope their portfolio links still work",
      talentProfile: "Verified deployed projects with automated uptime checks and working demos"
    }
  ];
</script>

{#key $page.url.pathname}
  <SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white via-purple-50/30 to-white">
  <!-- Hero Section -->
  <section class="pt-20 pb-16 px-4">
    <div class="max-w-6xl mx-auto">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Palette class="w-4 h-4" />
          Automated Code Analysis + Portfolio Verification
        </div>
        <h1 class="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Hire Frontend Developers<br />
          <span class="text-purple-600">Code Quality Analyzed. Portfolios Verified.</span>
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Stop guessing at code quality from resumes. We run automated security scans, performance analysis, and pattern detection on their GitHub repos. Browse verified portfolios with Lighthouse scores. Interview smarter, not longer.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="/drop-jd" class="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors inline-flex items-center gap-2">
            <FileText class="w-5 h-5" />
            Post Job Description
          </a>
          <a href="/how-it-works" class="bg-white text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors border-2 border-gray-200">
            See Analysis Examples
          </a>
        </div>
      </div>

      <!-- Stats -->
      <div class="grid md:grid-cols-4 gap-6 mb-16">
        {#each stats as stat (stat.value)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-lg transition-shadow">
            <div class="text-4xl font-bold text-purple-600 mb-2">{stat.value}</div>
            <div class="text-sm font-semibold text-gray-900 mb-1">{stat.label}</div>
            <div class="text-xs text-gray-600">{stat.description}</div>
          </div>
        {/each}
      </div>
    </div>
  </section>

	<div class="mx-auto max-w-4xl mb-10">
    <GlopayCtaHorizontal placement="hire-frontend-developers-post-hero" />
  </div>
    
  <!-- What We Actually Analyze -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        What We Actually Analyze (And How)
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        We're not claiming magic AI that perfectly judges developers. We run real static analysis tools, security scanners, and performance audits. The code tells the story - we just organize it so you can quickly spot quality signals.
      </p>
      
      <div class="grid md:grid-cols-2 gap-8">
        {#each codeAnalysis as analysis (analysis.category)}
          <div class="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-2 bg-purple-100 superellipse">
                <svelte:component this={analysis.icon} class="w-6 h-6 text-purple-600" />
              </div>
              <h3 class="text-xl font-bold text-gray-900">{analysis.category}</h3>
            </div>
            
            <ul class="space-y-2 mb-4">
              {#each analysis.checks as check (check)}
                <li class="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 class="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <span>{check}</span>
                </li>
              {/each}
            </ul>
            
            <div class="pt-4 border-t border-gray-200">
              <p class="text-sm text-purple-700 font-medium italic">
                {analysis.value}
              </p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Real Analysis Examples -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Real Analysis Output Examples
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Here's what our automated analysis actually looks like. Specific, actionable findings - not vague AI summaries.
      </p>
      
      <div class="grid md:grid-cols-2 gap-6">
        {#each realAnalysisExamples as example (example.finding)}
          <div class="bg-white p-6 rounded-xl border-2 border-{example.color === 'red' ? 'red' : example.color === 'orange' ? 'orange' : 'green'}-200 hover:shadow-lg transition-shadow">
            <div class="flex items-start gap-3 mb-3">
              <div class="w-3 h-3 superellipse bg-{example.color === 'red' ? 'red' : example.color === 'orange' ? 'orange' : 'green'}-500 shrink-0 mt-1.5"></div>
              <div>
                <h3 class="font-bold text-gray-900 mb-2">{example.finding}</h3>
                <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded font-mono mb-3">
                  {example.detail}
                </p>
                <p class="text-sm text-gray-700">
                  <strong>Impact:</strong> {example.impact}
                </p>
              </div>
            </div>
          </div>
        {/each}
      </div>
      
      <div class="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-xl">
        <div class="flex items-start gap-4">
          <BarChart3 class="w-6 h-6 text-blue-600 shrink-0 mt-1" />
          <div>
            <h3 class="font-bold text-gray-900 mb-2">These Are Real, Verifiable Metrics</h3>
            <p class="text-gray-700 mb-3">
              Every finding links to the specific file and line number. Nothing vague like "good code quality" - concrete issues you can discuss in interviews or verify yourself. ESLint rules, security scanners, and complexity calculators don't lie.
            </p>
            <p class="text-sm text-blue-700 italic">
              Think of this as intelligent pre-screening: we help you spend 30 minutes reviewing strong candidates instead of 3 hours with everyone who applied.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Seniority Indicators -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Seniority Indicators We Can Detect
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        We don't claim to perfectly determine if someone is "senior" - that's subjective and context-dependent. But we can objectively identify patterns that correlate with experience level. You make the final judgment.
      </p>
      
      <div class="overflow-x-auto">
        <table class="w-full bg-white rounded-xl border border-gray-200">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-4 text-left text-sm font-bold text-gray-900">Signal</th>
              <th class="px-6 py-4 text-left text-sm font-bold text-red-600">Junior Pattern</th>
              <th class="px-6 py-4 text-left text-sm font-bold text-green-600">Senior Pattern</th>
              <th class="px-6 py-4 text-center text-sm font-bold text-gray-900">Automatable</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            {#each seniorityIndicators as indicator (indicator.senior)}
              <tr class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 font-medium text-gray-900">{indicator.signal}</td>
                <td class="px-6 py-4 text-sm text-gray-600">{indicator.junior}</td>
                <td class="px-6 py-4 text-sm text-gray-600">{indicator.senior}</td>
                <td class="px-6 py-4 text-center">
                  <span class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium {indicator.automatable === true ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}">
                    {indicator.automatable === true ? '✓ Yes' : '⚠ Partially'}
                  </span>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      
      <div class="mt-8 bg-purple-50 border border-purple-200 p-6 rounded-xl">
        <div class="flex items-start gap-4">
          <TrendingUp class="w-6 h-6 text-purple-600 shrink-0 mt-1" />
          <div>
            <h3 class="font-bold text-gray-900 mb-2">Our Analysis Shows Patterns, You Assess Fit</h3>
            <p class="text-gray-700">
              A developer with 90% test coverage, strict TypeScript, proper error handling, and optimized performance is <em>likely</em> more experienced than someone with none of those. But context matters - maybe they're junior on an excellent team, or senior but working on side projects. Our analysis gives you data points for better interview questions and faster screening decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Pain Points -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Problems with Traditional Frontend Hiring
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Without automated code analysis, you're either spending hours reviewing every repo manually or interviewing developers blindly.
      </p>
      <div class="grid md:grid-cols-2 gap-8">
        {#each painPoints as point (point.problem)}
          <div class="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="flex items-start gap-4 mb-4">
              <div class="p-3 bg-red-100 superellipse shrink-0">
                <svelte:component this={point.icon} class="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 mb-2">{point.problem}</h3>
                <p class="text-gray-600 italic mb-3">"{point.reality}"</p>
              </div>
            </div>
            <div class="pl-16 pt-4 border-t border-gray-100">
              <div class="flex items-start gap-2">
                <CheckCircle2 class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <p class="text-gray-700"><strong>Automated Solution:</strong> {point.solution}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- What We Do -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Our Four-Layer Verification Process
      </h2>
      
      <div class="grid md:grid-cols-2 gap-8">
        {#each whatWeDo as item (item.title)}
          <div class="bg-gradient-to-br from-purple-50 to-white p-8 rounded-xl border border-purple-100 hover:shadow-lg transition-shadow">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-2 bg-purple-600 superellipse">
                <svelte:component this={item.icon} class="w-6 h-6 text-white" />
              </div>
              <h3 class="text-xl font-bold text-gray-900">{item.title}</h3>
            </div>
            <p class="text-gray-700 mb-4">{item.description}</p>
            <div class="flex flex-wrap gap-2">
              {#each item.examples as example (example)}
                <span class="px-3 py-1 bg-white border border-purple-200 superellipse text-xs text-purple-700 font-medium">
                  {example}
                </span>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Portfolio Types -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Match by Project Type + Verify Implementation
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        We don't just check if they've built e-commerce sites - we verify how they built them. Payment integration quality, cart state management, responsive product grids.
      </p>
      <div class="grid md:grid-cols-2 gap-6">
        {#each portfolioTypes as portfolio (portfolio.type)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <h3 class="text-lg font-bold text-gray-900 mb-2">{portfolio.type}</h3>
            <p class="text-gray-700 text-sm mb-3">{portfolio.description}</p>
            <div class="pt-3 border-t border-gray-200">
              <p class="text-xs text-purple-700 font-medium">
                {portfolio.verification}
              </p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Comparison -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Traditional Screening vs Automated Analysis
      </h2>
      <div class="space-y-6">
        {#each comparisonPoints as point (point.traditional)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
            <div class="grid md:grid-cols-2 gap-6">
              <div class="flex items-start gap-3">
                <div class="w-10 h-10 superellipse bg-red-100 flex items-center justify-center shrink-0 mt-1">
                  <span class="text-xs text-red-600 font-bold">✕</span>
                </div>
                <p class="text-gray-600">{point.traditional}</p>
              </div>
              <div class="flex items-start gap-3 md:border-l md:border-gray-200 md:pl-6">
                <div class="w-10 h-10 superellipse bg-green-100 flex items-center justify-center shrink-0 mt-1">
                  <CheckCircle2 class="w-4 h-4 text-green-600" />
                </div>
                <p class="text-gray-900 font-medium">{point.talentProfile}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Honest About Limitations -->
  <section class="py-16 px-4 bg-gradient-to-r from-blue-50 to-indigo-50">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-6 text-center">
        What We Don't Claim
      </h2>
      <div class="bg-white p-8 rounded-xl border-2 border-blue-200">
        <div class="space-y-4">
          <div class="flex items-start gap-3">
            <div class="w-6 h-6 superellipse bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
              <span class="text-xs text-gray-600">✕</span>
            </div>
            <p class="text-gray-700">
              <strong>We don't replace interviews.</strong> Automated analysis catches red flags and highlights strengths, but you still need to assess problem-solving, communication, and culture fit.
            </p>
          </div>
          
          <div class="flex items-start gap-3">
            <div class="w-6 h-6 superellipse bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
              <span class="text-xs text-gray-600">✕</span>
            </div>
            <p class="text-gray-700">
              <strong>We can't judge architectural decisions without context.</strong> Maybe they didn't write tests because it's a prototype. Maybe the "bad" pattern was a team standard. Our analysis raises questions, not conclusions.
            </p>
          </div>
          
          <div class="flex items-start gap-3">
            <div class="w-6 h-6 superellipse bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
              <span class="text-xs text-gray-600">✕</span>
            </div>
            <p class="text-gray-700">
              <strong>We can't guarantee side projects reflect work quality.</strong> Their personal repos might be experiments or old code. But patterns across multiple projects are meaningful signals.
            </p>
          </div>
        </div>
        
        <div class="mt-6 pt-6 border-t border-gray-200">
          <p class="text-gray-900 font-medium">
            <strong>What we DO promise:</strong> Objective technical analysis that saves you hours of manual screening. You interview fewer people, but the ones you talk to have cleared basic quality bars. Better hiring decisions, faster.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Frequently Asked Questions
      </h2>
      <div class="space-y-6">
        {#each faqs as faq (faq.question)}
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
            <p class="text-gray-700 leading-relaxed">{faq.answer}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl font-bold text-white mb-6">
        Stop Guessing at Code Quality. Start Analyzing It.
      </h2>
      <p class="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
        Post your job description. We'll match you with frontend developers whose code has been analyzed for security, performance, and quality. Browse their verified portfolios with Lighthouse scores. Interview with confidence.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="/drop-jd" class="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-colors inline-flex items-center justify-center gap-2">
          <FileText class="w-5 h-5" />
          Post Job Description - Free
        </a>
        <a href="/how-it-works" class="bg-purple-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-400 transition-colors">
          See Analysis Examples
        </a>
      </div>
      <p class="text-purple-200 mt-6 text-sm flex items-center justify-center gap-2">
        <Shield class="w-4 h-4" /> 18+ automated checks • Security scans • Lighthouse audits • No subscription fees
      </p>
    </div>
  </section>
</div>